/**
 * Converts a date/datetime string to the user's timezone.
 *
 * This utility handles dates coming from the backend (typically in UTC)
 * and converts them to the user's local timezone for display.
 *
 * @author Milton Filho
 */
import moment from 'moment-timezone';
import { Moment } from 'moment';
import { getUserTimezone } from './getUserTimezone';

/**
 * Converts a date string to a Moment object in the user's timezone.
 *
 * @param dateString - The date string to convert (typically ISO 8601 from API)
 * @param format - Optional format string if the input is not ISO 8601
 * @returns A Moment object in the user's timezone
 *
 * @example
 * // From API response in UTC
 * toUserTimezone('2024-07-07T07:15:00.000Z') // Returns moment in user's local timezone
 *
 * @example
 * // With specific format
 * toUserTimezone('2024-07-07', 'YYYY-MM-DD') // Returns moment in user's local timezone
 */
export const toUserTimezone = (
  dateString: string | null | undefined,
  format?: string,
): Moment => {
  if (!dateString) {
    return moment.invalid();
  }

  const userTimezone = getUserTimezone();

  // Parse the date string (moment-timezone will handle UTC automatically)
  const parsed = format
    ? moment.tz(dateString, format, userTimezone)
    : moment.tz(dateString, userTimezone);

  // If the input includes timezone info (like 'Z' or offset), convert to user timezone
  // Otherwise, assume it's already in the correct timezone
  if (dateString.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
    return moment(dateString).tz(userTimezone);
  }

  return parsed;
};

/**
 * Gets the current moment in the user's timezone.
 *
 * @returns A Moment object representing "now" in the user's timezone
 */
export const nowInUserTimezone = (): Moment => {
  const userTimezone = getUserTimezone();
  return moment().tz(userTimezone);
};
